import { Component, Input, OnDestroy, OnInit } from "@angular/core"
import { Router } from "@angular/router"
import { Subscription } from "rxjs"
import { ResourceService } from "../../../services/resource.service"
import { UserService } from "../../../services/user.service"
import { ModalRef } from "../../../shared-components/modal/modal-ref"
import { ModalService } from "../../../shared-components/modal/modal.service"
import { AssignModalComponent } from "../../../shared-components/modals/assign-modal/assign-modal.component"
import { AuthModalComponent } from "../../../shared-components/modals/auth-modal/auth-modal.component"
import Utils from "../../../utils/utils"
import { NotificationsService } from "angular2-notifications"

declare var window: any

@Component({
    selector: "app-share-assign-modal",
    templateUrl: "./share-assign-modal.component.html",
    styleUrls: ["./share-assign-modal.component.scss"],
})
export class ShareAssignModalComponent implements OnDestroy {
    public googleClassroomImage
    public remindImage
    public notAvailableText =
        "This resource is not available for assignment, " +
        "due to content rights or other restrictions."
    @Input() public titleToShare: string
    @Input() public resourceCode: string
    @Input() public isAssignAvailable?: boolean = true

    public isResourceUnAssignable
    public activityData
    public shareLink: string = ""
    public selectedAction = {
        assign: false,
        quickAssign: false,
        social: false,
        codeLink: false,
    }
    private isWaitingForAssignCreation = false

    public subscriptions: Subscription[] = []

    constructor(
        public activeModal: ModalRef,
        private resourceService: ResourceService,
        public userService: UserService,
        private modalService: ModalService,
        private notificationService: NotificationsService,
        private router: Router
    ) {
        this.googleClassroomImage = require("../../../../assets/google_classroom_x48_yellow_stroke_icon.png").default
        this.remindImage = require("../../../../assets/btn_remind_share_light_round_normal_2x.png").default
    }

    public resetSelectedAction() {
        this.selectedAction = {
            assign: false,
            quickAssign: false,
            social: false,
            codeLink: false,
        }
    }

    public assignToClass() {
        this.createAssignment((activityData) => {
            this.selectedAction.assign = true
            if (activityData.isAssignable) {
                this.activeModal.close()
                this.modalService.open(AssignModalComponent, {
                    size: "lg",
                    windowClass: "full-screen-modal",
                    data: {
                        activityData: activityData,
                    },
                })
            }
        })
    }

    public copyLinkByElementId(elemId) {
        Utils.copyLinkByElementId(elemId)
    }

    public onClickAssignCode() {
        this.createAssignment((activityData) => {
            this.selectedAction.quickAssign = true
            if (activityData.isAssignable) {
                this.activityData = activityData
            }
        })
    }

    public clickUseLessonBuilder(event) {
        event.preventDefault()
        this.createAssignment((activityData) => {
            if (activityData.isAssignable) {
                window.location = `/tools/lessonbuilder/edit?code=${activityData.activity.code}`
            }
        })
    }

    private createAssignment(callback) {
        if (this.isWaitingForAssignCreation) {
            return
        }
        if (this.doLoggedOutActions()) {
            return
        }
        this.isWaitingForAssignCreation = true
        this.subscriptions.push(
            this.resourceService.getOrCreateActivity(this.resourceCode).subscribe(
                (activityData: any) => {
                    this.isWaitingForAssignCreation = false
                    this.resetSelectedAction()

                    if (!activityData.isAssignable) {
                        this.isResourceUnAssignable = true
                    }
                    callback(activityData)
                },
                (error) => {
                    this.isWaitingForAssignCreation = false
                }
            )
        )
    }

    public onClickToGoogleClassroom(event) {
        event.preventDefault()
        if (this.isAssignAvailable) {
            this.resourceService.getGoogleClassroomSharableLink(
                this.resourceCode,
                this.userService.isLoggedIn()
            )
        } else {
            Utils.shareLinkToGC()
        }
    }

    public onClickRemind(event) {
        event.preventDefault()
        const remindText = encodeURIComponent('"' + this.titleToShare + '" on PBS LearningMedia')
        const composedUrl =
            "https://www.remind.com/v1/share?url=" +
            window.location.origin +
            window.location.pathname +
            "&referrer=" +
            window.location.origin +
            window.location.pathname +
            "&text=" +
            remindText
        window.open(composedUrl, null, "width=1024, height=600,toolbar=no, scrollbars=yes")
    }

    public onClickPinterest(event) {
        event.preventDefault()
    }

    public onClickShareLink(event) {
        this.resetSelectedAction()
        this.selectedAction.codeLink = true
        this.shareLink = window.location.origin + window.location.pathname
    }

    public onClickSocial() {
        this.resetSelectedAction()
        this.selectedAction.social = true
    }

    public onClickEmail() {
        this.resetSelectedAction()
        const currentUrl = window.location.href
        Utils.copyTextToClipboard(currentUrl)
        this.notificationService.success(
            "Url copied",
            "The current resource URL was copied to the clipboard",
            Utils.alertConfiguration
        )
        window.location.href = `mailto:?subject=PBS LearningMedia: ${this.titleToShare}&body=From PBS LearningMedia:%0A${this.titleToShare}%0A${currentUrl}`
    }

    public doLoggedOutActions() {
        if (!this.userService.isLoggedIn()) {
            const localStorageKey = this.resourceService.getLocalStorageShareAssignModalKey()
            window.localStorage.setItem(localStorageKey, true)
            this.activeModal.close()
            this.modalService.open(AuthModalComponent, {
                data: {
                    description:
                        "Log in to PBS LearningMedia and unlock assigning functionality and tons of features that will help you bring the world to your classroom",
                },
            })

            return true
        }
        return false
    }

    public isResourceAssignable() {
        return this.userService.isLoggedIn() && this.activityData
    }

    public isResourceUnassignable() {
        return this.userService.isLoggedIn() && this.isResourceUnAssignable
    }

    public showOnlyShareMessage() {
        return !this.userService.isLoggedIn() || !this.activityData || this.isResourceUnAssignable
    }

    public ngOnDestroy() {
        this.subscriptions.map((subscription) => subscription.unsubscribe())
    }
}
